/**
 * copyText: copy to the clipboard, wait for it to finish, and tell the user whether it worked.
 *
 *   await copyText(apiKey, "API key")         toast "API key copied" or "Couldn't copy the API key"
 *   await copyText(json)                       toast "Copied"
 *   const ok = await copyText(url, "Link", { silent: true })   no toast, just the result
 *
 * Uses navigator.clipboard when available and falls back to a hidden textarea + execCommand for http pages and
 * older browsers. Returns true on success. Toasts go through the global `toast` helper in context/ToastContext.
 */
import { toast } from "@/context/ToastContext";

async function writeClipboard(text: string): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-1000px";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  const selection = document.getSelection();
  const previous = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  ta.select();
  try {
    if (!document.execCommand("copy")) throw new Error("Copy command was rejected");
  } finally {
    document.body.removeChild(ta);
    if (previous && selection) {
      selection.removeAllRanges();
      selection.addRange(previous);
    }
  }
}

// "API key" stays "API key"; "Link" becomes "link" mid-sentence.
const midSentence = (s: string) => (/^[A-Z]{2,}/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1));

export async function copyText(text: string, what?: string, opts: { silent?: boolean } = {}): Promise<boolean> {
  try {
    await writeClipboard(text);
    if (!opts.silent) toast.success(what ? `${what} copied` : "Copied");
    return true;
  } catch {
    if (!opts.silent) toast.error(what ? `Couldn't copy the ${midSentence(what)}. Select it and copy it manually.` : "Couldn't copy. Select the text and copy it manually.");
    return false;
  }
}
